import { useState, useMemo } from 'react';
import { Link, useLocation } from '@tanstack/react-router';
import { useAtomValue } from 'jotai';
import { useTestMode } from '@/hooks/useTestMode';
import { organizationDataAtom, campaignsDataAtom } from '@/store';
import {
  Sidebar,
  SidebarContent,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import { navigation } from './constants';
import { OrganizationSelectionModal } from './organization-selection-modal';
import { useOrganizationInitialization } from '@/hooks/useOrganizationInitialization';
import { NotificationsMenu } from '@/components/top-bar/notifications-menu';
import { UserMenu } from '@/components/top-bar/user-menu';
import { OrgSwitcher } from '@/components/top-bar/org-switcher';
import { AdminMenu } from '@/components/top-bar/admin-menu';
import { useUserProfile } from '@/contexts/user.context';
import type { OrganizationDataWithMembership } from '@/lib/types';

interface NavigationItemProps {
  item: {
    name: string;
    to: string;
    icon: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  };
  location: {
    pathname: string;
  };
  showBadge?: boolean;
}

function NavigationItem({ item, location, showBadge }: NavigationItemProps) {
  // Active when the current path IS the item's route or a child of it, matched at
  // a path-segment boundary. A plain startsWith lit up /performance on both
  // /performance-v2 and /performance-consolidated (they share the prefix).
  const isActive =
    location.pathname === item.to || location.pathname.startsWith(`${item.to}/`);

  return (
    <SidebarMenuItem className='rounded-lg'>
      <SidebarMenuButton
        asChild
        className={`gap-3 text-[13px] py-5 px-4 group-data-[collapsible=icon]:!py-5 font-normal font-sans rounded-lg outline-none border-none focus-visible:ring-2 focus-visible:ring-[#290BD9] focus-visible:ring-inset ${isActive ? 'text-[#290BD9] bg-[#290BD9]/10 !cursor-default' : 'text-gray-900 hover:bg-gray-50 !cursor-pointer'}`}
      >
        <Link to={item.to}>
          <item.icon className='!h-5 !w-5' strokeWidth={1.5} />
          <span>{item.name}</span>
          {showBadge && (
            <span className="ml-auto h-4 w-4 rounded-full bg-red-500 text-white text-[9px] font-bold flex items-center justify-center shrink-0">!</span>
          )}
        </Link>
      </SidebarMenuButton>
    </SidebarMenuItem>
  );
}

export function NavigationSidebar() {
  const location = useLocation();
  const { state } = useSidebar();
  const [isOrgModalOpen, setIsOrgModalOpen] = useState(false);
  const [isAdminHidden, setIsAdminHidden] = useState(false);
  const { organizations, organizationData, switchOrganization } = useOrganizationInitialization();
  const testMode = useTestMode();
  const orgData = useAtomValue(organizationDataAtom);
  const campaigns = useAtomValue(campaignsDataAtom);
  const budgetNeedsAttention = testMode && !orgData?.max_spend && campaigns.some(c => (c.budget || 0) > 0);
  const { user } = useUserProfile();

  // A ``testModeOnly`` item shows in test mode; everything else always shows. The pre-GA
  // ``metaGated`` variant went away when Meta went live for everyone (#5976).
  const filteredNavigation = useMemo(() =>
    navigation.filter(item => {
      const gated = item as { testModeOnly?: boolean };
      if (gated.testModeOnly) return testMode;
      return true;
    }),
    [testMode]
  );

  const handleOrgModalClose = () => {
    setIsOrgModalOpen(false);
  };

  const handleOrganizationSelect = async (organization: OrganizationDataWithMembership) => {
    await switchOrganization(organization); // Secure switch with full cleanup
  };

  const isCollapsed = state === 'collapsed';

  return (
    <Sidebar className='bg-white border-r border-gray-200'>
      <SidebarContent className="flex flex-col">
        {/* Top: Org selector + collapse toggle */}
        <div className={`border-b border-gray-200 ${isCollapsed ? 'flex justify-center py-[5px]' : 'py-1'}`}>
          <OrgSwitcher isCollapsed={isCollapsed} />
        </div>

        {/* Nav items */}
        <SidebarMenu className="flex-1">
          {filteredNavigation.map((item) => (
            <NavigationItem
              key={item.name}
              item={item}
              location={location}
              showBadge={item.to === '/business' && budgetNeedsAttention}
            />
          ))}
        </SidebarMenu>

        {/* Bottom: Account, Notifications, Admin, Collapse toggle */}
        <div className="px-2 py-3">
          <div className={`flex items-center ${isCollapsed ? 'flex-col gap-3' : 'justify-evenly'}`}>
            <UserMenu />
            <NotificationsMenu />
            {user.role === 'ADMIN' && !isAdminHidden && <AdminMenu onHide={() => setIsAdminHidden(true)} />}
            <SidebarTrigger />
          </div>
        </div>
      </SidebarContent>
      <SidebarRail />
      {organizations && (
        <OrganizationSelectionModal
          isOpen={isOrgModalOpen}
          onClose={handleOrgModalClose}
          organizations={organizations}
          selectedOrganization={organizationData || null}
          onOrganizationSelect={handleOrganizationSelect}
        />
      )}
    </Sidebar>
  );
}
